import { CheckCircle2, ShieldAlert, AlertTriangle } from "lucide-react";

/**
 * StabilityChip — surfaces "is this test built on robust semantic
 * selectors, or does it rely on fragile pixel-offset fallbacks?".
 *
 * Tier rules (computed from the test's `steps` array, no server call):
 *   - **Stable**         — every click step has a `clickTarget`
 *                          (semantic, geometry-agnostic). Includes
 *                          tests with no click steps at all.
 *   - **Mostly stable**  — 1–2 click steps lack a `clickTarget` AND
 *                          fewer than 25 % of all clicks do.
 *   - **Low stability**  — 3+ clicks lack `clickTarget`, or ≥ 25 %
 *                          of clicks do. These tests are likely to
 *                          break on CSS / responsive / theme drift.
 *
 * A click step is "fragile" when it has no `clickTarget` — regardless
 * of whether it also has `click_offset`. Both pixel-offset fallback
 * and ancestor centre-click are vulnerable to layout shift; only
 * semantic clickTarget targeting is fully durable.
 *
 * Designed as a compact inline chip so it can sit next to the test
 * name in list views, the test detail header, etc.
 */
export function StabilityChip({ steps, testId, compact = false }) {
  const tier = computeStabilityTier(steps);
  if (!tier) return null;

  const styles = {
    stable: {
      bg: "bg-[#ECFDF5]",
      text: "text-[#047857]",
      border: "border-[#10B981]",
      Icon: CheckCircle2,
      label: "Stable",
      title: "All click steps use semantic clickTarget selectors. Robust to layout / responsive / theme changes.",
    },
    mostly: {
      bg: "bg-[#FFFBEB]",
      text: "text-[#B45309]",
      border: "border-[#F59E0B]",
      Icon: ShieldAlert,
      label: "Mostly stable",
      title: `${tier.fragileCount} of ${tier.totalClicks} click step(s) lack a semantic clickTarget — they fall back to pixel-offset or centre-click and may break on layout drift. Re-record those steps when convenient.`,
    },
    low: {
      bg: "bg-[#FEF2F2]",
      text: "text-[#B91C1C]",
      border: "border-[#EF4444]",
      Icon: AlertTriangle,
      label: "Low stability",
      title: `${tier.fragileCount} of ${tier.totalClicks} click step(s) lack a semantic clickTarget. This test is at risk of breaking on small CSS / theme / responsive changes — re-record to harden.`,
    },
  }[tier.key];

  const { Icon, label, bg, text, border, title } = styles;
  return (
    <span
      className={`inline-flex items-center gap-1 ${bg} ${text} border ${border} rounded-full font-semibold uppercase tracking-wide ${
        compact ? "px-1.5 py-0 text-[9px]" : "px-2 py-0.5 text-[10px]"
      }`}
      title={title}
      data-testid={testId || `stability-chip-${tier.key}`}
      data-tier={tier.key}
    >
      <Icon size={compact ? 10 : 11} strokeWidth={2.5} />
      {label}
    </span>
  );
}

/** Pure helper — exported for tests + sortable columns. Returns
 *  `{ key, fragileCount, totalClicks }` where `key` ∈ {"stable",
 *  "mostly", "low"}. Returns `null` for an empty/missing steps array.
 *
 *  Fragility rules (aligned with the runner's semantic click pipeline):
 *    - Click step with `clickTarget` → semantic icon click, stable.
 *    - Click step WITHOUT `clickTarget` AND `recordedSchemaVersion >= 3`
 *      → v3 recorder explicitly states the literal click target was the
 *      ancestor itself. Ancestor centre-click is semantic. Stable.
 *    - Click step WITHOUT `clickTarget` AND ANY OTHER step in the same
 *      recording has a `clickTarget` → retroactive heuristic: proves
 *      the recorder was clickTarget-capable at recording time, so the
 *      absence on this step is also an "ancestor click" signal. Stable.
 *    - Click step WITHOUT `clickTarget` AND the recording has zero
 *      `clickTarget`s anywhere AND schema < 3 → pre-clickTarget
 *      recorder couldn't distinguish ancestor vs child click. Replay
 *      falls through to pixel-offset. Fragile. */
export function computeStabilityTier(steps) {
  if (!Array.isArray(steps)) return null;
  const clicks = steps.filter((s) => s && s.action === "click");
  const totalClicks = clicks.length;
  // Retroactive heuristic: if ANY step in this recording captured a
  // `clickTarget`, the recorder must have been clickTarget-aware
  // (iteration 19+), so missing `clickTarget` on sibling steps is an
  // explicit "click was on ancestor" signal, not a recorder gap.
  const recordingIsClickTargetAware = steps.some((s) => s && s.clickTarget);
  const fragileCount = clicks.filter((s) => {
    if (s.clickTarget) return false;
    const schema = Number(s.recordedSchemaVersion) || 1;
    if (schema >= 3) return false;
    if (recordingIsClickTargetAware) return false;
    return true;
  }).length;
  if (totalClicks === 0 || fragileCount === 0) {
    return { key: "stable", fragileCount: 0, totalClicks };
  }
  // "Mostly stable" guardrail: must be both ≤ 2 absolute AND < 25 %.
  // Above either threshold escalates to "low stability".
  if (fragileCount <= 2 && fragileCount / totalClicks < 0.25) {
    return { key: "mostly", fragileCount, totalClicks };
  }
  return { key: "low", fragileCount, totalClicks };
}
